import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  PhoneCall, Clock, CheckCircle2, XCircle, User, Phone, Mail,
  MessageSquare, ChevronDown, ChevronUp, Filter, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { value: 'pending', label: 'Pending', color: '#F97316', icon: Clock },
  { value: 'contacted', label: 'Contacted', color: '#4A90E2', icon: PhoneCall },
  { value: 'resolved', label: 'Resolved', color: '#00A6A0', icon: CheckCircle2 },
  { value: 'cancelled', label: 'Cancelled', color: '#94A3B8', icon: XCircle },
];

export function CallbackRequests() {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [adminNotes, setAdminNotes] = useState({});

  useEffect(() => { fetchRequests(); }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const { data } = await axios.get(`${API_URL}/api/callback-requests${params}`, { withCredentials: true });
      setRequests(data);
    } catch (err) {
      console.error('Failed to fetch callback requests:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const payload = { status: newStatus };
      if (adminNotes[id]) payload.admin_notes = adminNotes[id];
      await axios.put(`${API_URL}/api/callback-requests/${id}`, payload, { withCredentials: true });
      toast.success(`Request marked as ${newStatus}`);
      fetchRequests();
    } catch (err) {
      toast.error('Failed to update request');
    }
  };

  const saveNotes = async (id) => {
    try {
      await axios.put(`${API_URL}/api/callback-requests/${id}`, { admin_notes: adminNotes[id] || '' }, { withCredentials: true });
      toast.success('Notes saved');
    } catch (err) {
      toast.error('Failed to save notes');
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const contactedCount = requests.filter(r => r.status === 'contacted').length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="callback-requests-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
            Callback Requests
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage care seeker enquiries — {pendingCount} pending, {contactedCount} contacted
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {STATUSES.map(s => {
            const Icon = s.icon;
            const count = requests.filter(r => r.status === s.value).length;
            return (
              <button key={s.value}
                onClick={() => setStatusFilter(statusFilter === s.value ? '' : s.value)}
                className={`glass-card p-4 text-left transition-all ${statusFilter === s.value ? 'ring-2' : ''}`}
                style={statusFilter === s.value ? { borderColor: s.color, boxShadow: `0 0 0 2px ${s.color}30` } : {}}
                data-testid={`filter-${s.value}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4" style={{ color: s.color }} />
                  <span className="text-xs text-muted-foreground">{s.label}</span>
                </div>
                <p className="text-2xl font-heading font-bold text-foreground">{count}</p>
              </button>
            );
          })}
        </div>

        {statusFilter && (
          <button onClick={() => setStatusFilter('')} className="text-sm text-[#00A6A0] hover:underline flex items-center gap-1">
            <X className="w-3 h-3" /> Clear filter
          </button>
        )}

        {/* Requests List */}
        {isLoading ? (
          <div className="text-center py-12"><div className="spinner mx-auto" /></div>
        ) : requests.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="p-12 text-center">
              <PhoneCall className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                No Callback Requests
              </h3>
              <p className="text-muted-foreground">
                {statusFilter ? 'No requests with this status' : 'When care seekers request callbacks, they will appear here'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {requests.map(req => {
              const isExpanded = expandedId === req.id;
              const statusInfo = STATUSES.find(s => s.value === req.status) || STATUSES[0];
              const StatusIcon = statusInfo.icon;
              return (
                <Card key={req.id} className="glass-card" data-testid={`callback-${req.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${statusInfo.color}15` }}>
                          <StatusIcon className="w-5 h-5" style={{ color: statusInfo.color }} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-heading font-semibold text-foreground">{req.name}</h3>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                              style={{ backgroundColor: `${statusInfo.color}15`, color: statusInfo.color }}>
                              {statusInfo.label}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-3 mt-1.5 text-sm text-muted-foreground">
                            <a href={`tel:${req.phone}`} className="flex items-center gap-1 hover:text-foreground">
                              <Phone className="w-3.5 h-3.5" />{req.phone}
                            </a>
                            {req.email && (
                              <a href={`mailto:${req.email}`} className="flex items-center gap-1 hover:text-foreground">
                                <Mail className="w-3.5 h-3.5" />{req.email}
                              </a>
                            )}
                          </div>
                          {req.care_level_label && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Care needed: <span className="text-foreground">{req.care_level_label}</span>
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {req.created_at ? new Date(req.created_at).toLocaleString() : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {req.status === 'pending' && (
                          <Button size="sm" className="btn-primary" onClick={() => updateStatus(req.id, 'contacted')} data-testid={`mark-contacted-${req.id}`}>
                            <PhoneCall className="w-3.5 h-3.5 mr-1" />Contacted
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setExpandedId(isExpanded ? null : req.id)}>
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-border space-y-4 animate-in slide-in-from-top-1">
                        {req.message && (
                          <div>
                            <h4 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-1.5">
                              <MessageSquare className="w-3.5 h-3.5" />Message from care seeker
                            </h4>
                            <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">{req.message}</p>
                          </div>
                        )}

                        {/* Admin notes */}
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-1">Internal Notes</h4>
                          <div className="flex gap-2">
                            <textarea
                              value={adminNotes[req.id] ?? req.admin_notes ?? ''}
                              onChange={(e) => setAdminNotes(prev => ({ ...prev, [req.id]: e.target.value }))}
                              placeholder="Add internal notes about this request..."
                              rows={2}
                              className="flex-1 rounded-md border border-border bg-card text-foreground px-3 py-2 text-sm resize-none"
                              data-testid={`notes-${req.id}`}
                            />
                            <Button variant="outline" size="sm" className="btn-secondary self-end" onClick={() => saveNotes(req.id)} data-testid={`save-notes-${req.id}`}>
                              Save
                            </Button>
                          </div>
                        </div>

                        {/* Status Actions */}
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-2">Update Status</h4>
                          <div className="flex flex-wrap gap-2">
                            {STATUSES.map(s => (
                              <Button
                                key={s.value}
                                variant={req.status === s.value ? 'default' : 'outline'}
                                size="sm"
                                className={req.status === s.value ? '' : 'text-muted-foreground'}
                                style={req.status === s.value ? { backgroundColor: s.color, color: '#fff' } : {}}
                                onClick={() => updateStatus(req.id, s.value)}
                                data-testid={`status-${s.value}-${req.id}`}
                              >
                                {s.label}
                              </Button>
                            ))}
                          </div>
                        </div>

                        {/* Contact shortcuts */}
                        <div className="flex gap-2">
                          <a href={`tel:${req.phone}`}>
                            <Button variant="outline" size="sm" className="btn-secondary" data-testid={`call-${req.id}`}>
                              <Phone className="w-3.5 h-3.5 mr-1.5" />Call
                            </Button>
                          </a>
                          {req.email && (
                            <a href={`mailto:${req.email}`}>
                              <Button variant="outline" size="sm" className="btn-secondary" data-testid={`email-${req.id}`}>
                                <Mail className="w-3.5 h-3.5 mr-1.5" />Email
                              </Button>
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default CallbackRequests;
